import { REGISTERED_APPS } from '../dissolve/types';
import { productsCoveredByDiscount } from '../api/candyrack';
import { getCart } from '../api/shared-app-requests';

import storage from '../storage';
import {
  loadCartAndCleanupAssociatedProductData,
  showInfoInCart,
} from './cart';
import {
  CANDYRACK_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
  CANDYRACK_DISCOUNT_CODE_LOCAL_STORAGE_KEY,
  CANDYRACK_CHECKOUT_BUTTON_DISCOUNT_CODE_LOCAL_STORAGE_KEY,
  CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
} from './constants';
import { logError, logInfo } from './logging';
import throttle from './throttle';

const FORMS =
  "form[action*='/checkout'], form[action*='/cart'], form[action*='/a/checkout']";

export async function saveDiscountCodeForCandyrack(
  code: string,
  variantId: number,
  discountedPrice: number,
  discount: string,
  relatedProductId: number,
) {
  // discountedPrice is used for tracking, discount for UI
  storage.setItem(CANDYRACK_DISCOUNT_CODE_LOCAL_STORAGE_KEY, code);
  storage.setItem(
    CANDYRACK_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    JSON.stringify({
      variantId,
      discountedPrice,
      discount,
      relatedProductId,
    }),
  );

  // this method doesn't work on iPhones
  fetch(`/discount/${code}`, {
    credentials: 'same-origin',
    redirect: 'manual',
  });

  updateDiscounts(REGISTERED_APPS.CANDYRACK);
}

export function saveDiscountForCandyRackCheckoutButton(
  discount: string,
  variantId: number,
) {
  storage.setItem(
    CANDYRACK_CHECKOUT_BUTTON_DISCOUNT_CODE_LOCAL_STORAGE_KEY,
    discount,
  );
  storage.setItem(
    CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    JSON.stringify({
      variantId,
      discount,
    }),
  );
}

export function removeCandyRackCheckoutButtonDiscount(
  discount: string,
  variantId: number,
) {
  const appliedDiscount = storage.getItem(
    CANDYRACK_CHECKOUT_BUTTON_DISCOUNT_CODE_LOCAL_STORAGE_KEY,
  );
  const discountedProduct = JSON.parse(
    storage.getItem(
      CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    ) || '{}',
  );

  if (
    discount === appliedDiscount &&
    discountedProduct?.variantId === variantId
  ) {
    storage.removeItem(
      CANDYRACK_CHECKOUT_BUTTON_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY,
    );
    storage.removeItem(
      CANDYRACK_CHECKOUT_BUTTON_DISCOUNT_CODE_LOCAL_STORAGE_KEY,
    );
  }
}

export function updateDiscounts(runningAppName: REGISTERED_APPS) {
  applyDiscount(runningAppName);
  updateCheckoutLinks(runningAppName);
}

function getDiscountCodeFromInput() {
  const inputs = document.querySelectorAll<HTMLInputElement>('[name=discount]');

  for (const input of Array.from(inputs)) {
    if (input.value) {
      return input.value;
    }
  }
}

export function getDiscountCode(runningAppName: REGISTERED_APPS): string {
  let code: string | null = null;
  if (REGISTERED_APPS.CANDYRACK === runningAppName) {
    code = storage.getItem(CANDYRACK_DISCOUNT_CODE_LOCAL_STORAGE_KEY);
  }
  if (!code || code === ' ') {
    code = getCookie('discount_code') || getDiscountCodeFromInput() || ' ';
  }
  return code;
}

function applyDiscount(runningAppName: REGISTERED_APPS) {
  const code = getDiscountCode(runningAppName);

  if (REGISTERED_APPS.CANDYRACK === runningAppName) {
    attachDiscountInfoForCandyrack(code);
  }

  window.chCouponCode = code;
  const forms = document.querySelectorAll<HTMLFormElement>(FORMS);

  Array.from(forms).forEach((form) => {
    const discountInput =
      form.querySelector<HTMLInputElement>('[name=discount]');
    if (!discountInput) {
      form.insertAdjacentHTML(
        'beforeend',
        `<input type="hidden" name="discount" value="${code}" />`,
      );
    } else if (discountInput.value.trim() === '' && code !== ' ') {
      discountInput.value = code;
    }
  });
}

function updateCheckoutLinks(runningAppName: REGISTERED_APPS) {
  const code = getDiscountCode(runningAppName);
  const weglotLanguage = window.Weglot && window.Weglot.getCurrentLang();
  const linkWithCode = `/checkout?discount=${code}${
    weglotLanguage ? `&locale=${weglotLanguage}` : ''
  }`;

  function checkoutClickHandler(event: MouseEvent) {
    event.preventDefault();
    event.stopImmediatePropagation();
    window.location.href = linkWithCode;
  }

  const nodes = document.querySelectorAll(
    '[href^="/checkout"],[name=return_to][value^="/checkout"],button[onclick],[data-component-checkout-button]',
  );
  Array.from(nodes).forEach((node) => {
    if (node instanceof HTMLAnchorElement) {
      node.href = linkWithCode;
    }
    if (node instanceof HTMLInputElement) {
      node.value = linkWithCode;
    }
    if (
      node instanceof HTMLButtonElement &&
      node.attributes
        .getNamedItem('onclick')
        ?.value?.match(/location.*checkout/)
    ) {
      node.onclick = checkoutClickHandler;
    }
    // FBBCApproved.com and TruLean.com
    if (
      node instanceof HTMLButtonElement &&
      node.attributes.getNamedItem('data-component-checkout-button')
    ) {
      node.onclick = checkoutClickHandler;
    }
  });
}

async function attachDiscountInfoForCandyrack(code: string) {
  if (!code.trim()) {
    return;
  }

  const { discount } = getDiscountedProductWithDiscount();
  if (!document.querySelector("a[href*='?variant=']")) {
    return;
  }

  const cart = await getCart();
  if (cart.items.length === 0) {
    return;
  }
  const productIds = cart.items.map((item: any) => item.product_id);
  const variantIds = cart.items.map((item: any) => item.variant_id);
  const variantsEligibleForDiscount = await productsCoveredByDiscount(
    code,
    productIds,
    variantIds,
  );
  showInfoInCart(variantsEligibleForDiscount, discount);
}

function watchForDrawerCart(runningAppName: REGISTERED_APPS) {
  const throttledApplyDiscount = throttle(
    () => applyDiscount(runningAppName),
    1000,
    true,
  );
  const throttledUpdateCheckoutLinks = throttle(
    () => updateCheckoutLinks(runningAppName),
    1000,
    true,
  );
  const throttledCleanupAssociatedProductData = throttle(
    () => loadCartAndCleanupAssociatedProductData(runningAppName),
    3000,
  );
  const callback = function (mutationsList: any) {
    throttledUpdateCheckoutLinks();
    throttledCleanupAssociatedProductData();
    for (const mutation of mutationsList) {
      if (mutation.type === 'childList') {
        for (var node of mutation.addedNodes) {
          if (
            (node.action &&
              (node.action.indexOf('/cart') > -1 ||
                node.action.indexOf('/checkout') > -1 ||
                node.action.indexOf('/a/checkout') > -1)) ||
            (node.querySelector && node.querySelector("a[href*='?variant=']")) // shopblends.com
          ) {
            throttledApplyDiscount();
          }
        }
      }
    }
  };

  try {
    const observer = new MutationObserver(callback);
    const targetNode = document.body;
    observer.observe(targetNode, { childList: true, subtree: true });
  } catch (error) {
    logError('Failed to setup drawer cart observer', runningAppName, error);
  }
}

export function clearDiscount() {
  storage.removeItem(CANDYRACK_DISCOUNT_CODE_LOCAL_STORAGE_KEY);
  storage.removeItem(CANDYRACK_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY);
}

export function getDiscountedProductWithDiscount() {
  const data = storage.getItem(CANDYRACK_DISCOUNTED_PRODUCT_LOCAL_STORAGE_KEY);
  if (data) {
    try {
      return JSON.parse(data);
    } catch {
      return {};
    }
  }
  return {};
}

export function handleDiscounts(runningAppName: REGISTERED_APPS) {
  updateDiscounts(runningAppName);
  watchForDrawerCart(runningAppName);
}

export function calculateDiscountedPrice(
  variantPrice: number,
  amount: number,
  amount_type?: string,
  apply_discount?: boolean,
) {
  if (apply_discount) {
    const discount =
      amount_type === 'fixed_amount' ? amount : (variantPrice * amount) / 100;
    return variantPrice - discount;
  } else {
    return variantPrice;
  }
}

export function cleanupDiscountData(
  cart: any,
  runningAppName: REGISTERED_APPS,
) {
  const { variantId, relatedProductId } = getDiscountedProductWithDiscount();
  const upsellProductCartItem = cart.items.find(
    (item: any) => item.variant_id === Number(variantId),
  );

  if (variantId && !upsellProductCartItem) {
    logInfo(
      `Variant ${variantId} not found. Removing discount info...`,
      runningAppName,
    );
    clearDiscount();
  }

  if (
    !cart.items.find(
      (item: any) => item.product_id === Number(relatedProductId),
    )
  ) {
    logInfo(
      `Related product ${relatedProductId} not found. Removing discount info...`,
      runningAppName,
    );
    clearDiscount();
    storage.setItem(CANDYRACK_DISCOUNT_CODE_LOCAL_STORAGE_KEY, ' ');
    applyDiscount(runningAppName);
  }
}

function getCookie(name: string) {
  return parseCookies()[name] || '';
}

function parseCookies(): { [key: string]: string } {
  const cookieParts = document.cookie.split(/; */).map((c) => {
    const [key, ...v] = c.split('=');
    let value = v.join('=');
    try {
      value = decodeURIComponent(value);
    } catch (e) {}
    return [key, value];
  });
  return cookieParts.reduce((object, [key, value]) => {
    return Object.assign(object, { [key]: value });
  }, {});
}
